import { Row, Col, Card, CardBody } from "react-bootstrap";
import { useEffect, useState } from "react";
import crudApi from "@serie3/common/api/crudApi";
import DashboardEndpoints from "@serie3/mymediaplanner/Modules/Dashboard/endpoints";
import { useTranslation } from "react-i18next";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import PlanOverview from "./components/Dashboard/Widgets/PlanOverview";
import PostTypes from "./components/Dashboard/Widgets/PostTypes";
import Stats from "./components/Dashboard/Widgets/Stats";
import Accountability from "./components/Dashboard/Widgets/Accountability";
import TopPosts from "./components/Dashboard/Widgets/TopPosts";
import FollowerGrowth from "./components/Dashboard/Widgets/FollowerGrowth";
import { useNavigate } from "react-router-dom";
import MonthYearPicker from "./components/Dashboard/MonthYearPicker";
import getDashboardUrl from "@serie3/mymediaplanner/Modules/Dashboard/Domain/getDashboardUrl";
import PostFormView from "./components/PostFormView";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [types] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [monthYear, setMonthYear] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${month}${year}`;
  });

  const navigate = useNavigate();

  const { t } = useTranslation();

  useEffect(() => {
    const getDashboardData = async () => {
      const url = getDashboardUrl(DashboardEndpoints.overview, monthYear);
      let response = await crudApi.justGet(url);
      if (response.ok) {
        setDashboardData(response.data);
      }
    };

    getDashboardData();
  }, [monthYear]);

  const daysUntilExpiration = (dateString) => {
    const now = new Date();
    const expirationDate = new Date(dateString);
    const timeDiff = expirationDate - now;
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24)); // Convert from milliseconds to days
  };

  if (!dashboardData) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80 }}>
        <span className="btn-spinner" style={{ width: 32, height: 32 }} />
      </div>
    );
  }

  return dashboardData ? (
    <>
      {dashboardData.error.length > 0 && (
        <div
          onClick={() => navigate("/invitations", { state: { tab: "failed" } })}
          style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 20px", marginBottom: 16, borderRadius: 12,
            background: "rgba(243,78,78,0.07)", border: "1px solid rgba(243,78,78,0.25)",
            borderLeft: "4px solid #f34e4e", cursor: "pointer",
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(243,78,78,0.12)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(243,78,78,0.07)"; }}
        >
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(243,78,78,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="uil uil-exclamation-triangle" style={{ fontSize: 18, color: "#f34e4e" }} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#c0001a" }}>{t("Backend.Dashboard.Errors")}</p>
            <p style={{ margin: 0, fontSize: 12, color: "#74788d" }}>{dashboardData.error.length} {t("Backend.Dashboard.ErrorsCount", "posting error(s) detected")}</p>
          </div>
          <i className="uil uil-arrow-right" style={{ fontSize: 18, color: "#f34e4e" }} />
        </div>
      )}

      {dashboardData.tokens &&
      dashboardData.tokens.localToken &&
      dashboardData.tokens.localToken.length > 0 ? (
        dashboardData.tokens.localToken.map((tokendata, index) => {
          const daysLeft = daysUntilExpiration(tokendata.expires);
          if (daysLeft > 10) return null;
          const isUrgent = daysLeft <= 5;
          const accent = isUrgent ? "#f34e4e" : "#f7cc53";
          const accentBg = isUrgent ? "rgba(243,78,78,0.07)" : "rgba(247,204,83,0.08)";
          const accentBorder = isUrgent ? "rgba(243,78,78,0.25)" : "rgba(247,204,83,0.35)";
          const iconColor = isUrgent ? "#f34e4e" : "#a07800";
          const iconBg = isUrgent ? "rgba(243,78,78,0.12)" : "rgba(247,204,83,0.15)";
          const labelColor = isUrgent ? "#c0001a" : "#7a5c00";
          return (
            <div key={index} style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 20px", marginBottom: 16, borderRadius: 12,
              background: accentBg, border: `1px solid ${accentBorder}`,
              borderLeft: `4px solid ${accent}`,
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="uil uil-hourglass" style={{ fontSize: 18, color: iconColor }} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: labelColor }}>
                  {tokendata.plattform_name} {t("Backend.Dashboard.TokenExpires")}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: "#74788d" }}>
                  {t("Backend.Dashboard.ExpiresOn", "Expires on")} {formatDate(tokendata.expires)} · {daysLeft} {t("Backend.Dashboard.DaysLeft", "days left")}
                </p>
              </div>
              <button
                onClick={() => navigate("/callbacks/linkedin")}
                style={{
                  padding: "6px 14px", borderRadius: 8, border: `1px solid ${accent}`,
                  background: "transparent", color: accent, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: "Montserrat, sans-serif", flexShrink: 0,
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = accentBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {t("Backend.Dashboard.Reconnect", "Reconnect")}
              </button>
            </div>
          );
        })
      ) : (
        <div
          onClick={() => navigate("/callbacks/linkedin")}
          style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 20px", marginBottom: 16, borderRadius: 12,
            background: "rgba(3,142,220,0.06)", border: "1px solid rgba(3,142,220,0.2)",
            borderLeft: "4px solid #038edc", cursor: "pointer",
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(3,142,220,0.1)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(3,142,220,0.06)"; }}
        >
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(3,142,220,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="uil uil-link" style={{ fontSize: 18, color: "#038edc" }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#038edc" }}>{t("Backend.Dashboard.ConnetToLinkedIn")}</p>
            <p style={{ margin: 0, fontSize: 12, color: "#74788d" }}>{t("Backend.Dashboard.ConnectHint", "Connect your LinkedIn account to start posting")}</p>
          </div>
          <i className="uil uil-arrow-right" style={{ fontSize: 18, color: "#038edc", marginLeft: "auto" }} />
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="page-section-header" style={{ marginBottom: 0 }}>
          <span className="page-section-header-icon" style={{ background: "rgba(3,142,220,0.12)" }}>
            <i className="uil uil-chart-bar" style={{ color: "#038edc", fontSize: 16 }} />
          </span>
          {t("Backend.Dashboard.Title", "Dashboard")}
        </h4>
        <MonthYearPicker label="" value={monthYear} setValue={setMonthYear} style={{ width: 160 }} />
      </div>

      <Row className="mb-4 g-3">
        {[
          { color: "#038edc", bg: "rgba(3,142,220,0.12)", icon: "uil-list-ul", label: t("Backend.Dashboard.TotalPosts"), value: dashboardData.posts.length },
          { color: "#51d28c", bg: "rgba(81,210,140,0.12)", icon: "uil-schedule", label: t("Backend.Dashboard.Schedules"), value: dashboardData.schedules.length },
          { color: "#f1734f", bg: "rgba(241,115,79,0.12)", icon: "uil-question", label: t("Backend.Dashboard.NotPosted"), value: dashboardData.notPosted.length },
        ].map(({ color, bg, icon, label, value }) => (
          <Col md={4} key={label}>
            <Card style={{ borderRadius: 16, border: "1px solid #eaecf0", borderLeft: `4px solid ${color}`, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <CardBody style={{ padding: "20px 24px" }}>
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={`uil ${icon}`} style={{ fontSize: 22, color }} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 12, color: "#9299a8", fontWeight: 500 }}>{label}</p>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 400, color: "#1a2236", lineHeight: 1.2 }}>{value}</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
      <Row className="align-items-stretch mb-4">
        <Col md={8}>
          <Stats monthYear={monthYear} />
        </Col>
        <Col md={4} className="d-flex flex-column gap-3">
          {dashboardData.posts.length > 0 && (
            <PlanOverview
              monthYear={monthYear}
              onEdit={(post) => { setEditPost(post); setShowEdit(true); }}
            />
          )}
          <TopPosts onEdit={(post) => { setEditPost(post); setShowEdit(true); }} />
        </Col>
      </Row>
      <Row className="mb-4 align-items-stretch">
        <Col md={8}>
          <FollowerGrowth monthYear={monthYear} />
        </Col>
        <Col md={4}>
          <Accountability monthYear={monthYear} />
        </Col>
      </Row>
      <Row>
        <PostTypes types={types} monthYear={monthYear} />
      </Row>
      <PostFormView
        showEdit={showEdit}
        setShowEdit={setShowEdit}
        editData={editPost}
        setEditData={setEditPost}
        dataList={posts}
        setDataList={setPosts}
      />
    </>
  ) : (
    <Row>
      <Col>Dashboard loading</Col>
    </Row>
  );
};

export default Dashboard;
